import type { Page } from '../components/PageConfigProvider/types';
import type { PositionedItem } from '../components/PageConfigProvider/types/item';
import { getLegacyTableDefinition } from '../components/PageConfigProvider/utils/plot-table-columns';
import { FilterId, type PrimaryFilterId } from '../filters/types/filters';
import type { ReportDocumentConfig } from '../report-document/report-document-types';

/**
 * Saves every chart with the `tableColumns` its legacy fields describe. The
 * legacy fields stay the source of truth until charts are edited through their
 * columns, so the columns are rebuilt on each save; a table authors its own.
 */
export const withLegacyTableColumns = (
  item: PositionedItem,
  primaryFilterId: PrimaryFilterId
): PositionedItem => {
  const { plot } = item;
  if (!plot || plot.chartType === 'table') return item;
  const { tableColumns: _stale, ...rest } = plot;
  const { tableColumns } = getLegacyTableDefinition(plot, primaryFilterId);
  return {
    ...item,
    plot: tableColumns.length > 0 ? { ...rest, tableColumns } : rest,
  };
};

export const serializePageTableColumns = <T extends Partial<Page>>(
  page: T
): T =>
  page.tabs
    ? {
        ...page,
        tabs: page.tabs.map((tab) => ({
          ...tab,
          tabContent: {
            ...tab.tabContent,
            items: tab.tabContent?.items?.map((item) =>
              withLegacyTableColumns(
                item,
                tab.tabContent.primaryFilter?.id ?? FilterId.COMPANY
              )
            ),
          },
        })),
      }
    : page;

export const serializeReportDocumentTableColumns = <
  T extends Partial<ReportDocumentConfig>,
>(
  document: T
): T => {
  if (!document.pages) return document;
  const primaryFilterId = document.primaryFilter?.id ?? FilterId.COMPANY;
  const withColumns = (item: PositionedItem) =>
    withLegacyTableColumns(item, primaryFilterId);
  return {
    ...document,
    pages: document.pages.map((page) => ({
      ...page,
      blocks: page.blocks.map((block) =>
        block.kind === 'columns'
          ? { ...block, items: block.items.map(withColumns) }
          : { ...block, item: withColumns(block.item) }
      ),
    })),
  };
};
